import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useChat } from "../context/ChatContext";
import { chatApi } from "../lib/realtime";
import { formatApiError } from "../lib/api";
import { OndaAvatar } from "../components/OndaAvatar";
import { MessageBubble } from "../components/MessageBubble";
import { ChatComposer } from "../components/ChatComposer";
import { PresenceDot, PresenceText } from "../components/Presence";
import { VerifiedBadge } from "../components/VerifiedBadge";
import { GroupMembersSheet } from "../components/GroupMembersSheet";
import { SmartSuggestions } from "../components/SmartSuggestions";
import { trackMessageSend } from "../lib/telemetry";

function groupByDay(messages) {
  const groups = [];
  let currentKey = null;
  for (const m of messages) {
    const d = new Date(m.timestamp);
    const key = d.toDateString();
    if (key !== currentKey) {
      groups.push({ key, label: formatDayLabel(d), items: [m] });
      currentKey = key;
    } else {
      groups[groups.length - 1].items.push(m);
    }
  }
  return groups;
}

function formatDayLabel(d) {
  const now = new Date();
  const dayMs = 86400000;
  const diff = Math.floor(
    (new Date(now).setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / dayMs,
  );
  if (diff === 0) return "Hoy";
  if (diff === 1) return "Ayer";
  return new Date(d).toLocaleDateString(undefined, {
    weekday: "long",
    day: "2-digit",
    month: "short",
  });
}

export default function ChatPage() {
  const { conversationId } = useParams();
  const { user } = useAuth();
  const {
    subscribeToMessages,
    subscribeToReads,
    setActiveConversationId,
    setActiveConversationFocused,
    refreshConversations,
    getPresence,
    refreshPresence,
    getTypingFor,
    sendTyping,
  } = useChat();
  const navigate = useNavigate();

  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);
  const hasSeenBottomRef = useRef(false);

  // Load conversation meta + messages
  useEffect(() => {
    let alive = true;
    if (!conversationId) return;
    setActiveConversationId(conversationId);
    setActiveConversationFocused(true);
    setLoading(true);
    (async () => {
      try {
        const [convs, msgs] = await Promise.all([
          chatApi.listConversations(),
          chatApi.listMessages(conversationId, { limit: 100 }),
        ]);
        if (!alive) return;
        const c = convs.find((x) => x.id === conversationId);
        setConv(c || null);
        setMessages(msgs);
        if (c?.other?.id) refreshPresence(c.other.id);
        try {
          await chatApi.markRead(conversationId);
          refreshConversations();
        } catch (_e) {
          /* non-fatal */
        }
      } catch (err) {
        if (alive) toast.error(formatApiError(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      setActiveConversationId(null);
    };
  }, [conversationId, setActiveConversationId, setActiveConversationFocused, refreshConversations, refreshPresence]);

  // Track focus for notification suppression
  useEffect(() => {
    const onVis = () => setActiveConversationFocused(!document.hidden);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", () => setActiveConversationFocused(true));
    window.addEventListener("blur", () => setActiveConversationFocused(false));
    return () => {
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [setActiveConversationFocused]);

  // Presence safety-net poll
  useEffect(() => {
    const otherId = conv?.other?.id;
    if (!otherId) return undefined;
    const t = setInterval(() => refreshPresence(otherId), 4000);
    return () => clearInterval(t);
  }, [conv, refreshPresence]);

  // Realtime message subscription
  useEffect(() => {
    if (!conversationId) return undefined;
    const unsub = subscribeToMessages(async (payload) => {
      if (payload.__poll) {
        try {
          const msgs = await chatApi.listMessages(conversationId, { limit: 100 });
          setMessages((prev) => {
            const pending = prev.filter(
              (m) =>
                (m.__status === "sending" || m.__status === "failed") &&
                !msgs.some((s) => s.id === m.id),
            );
            return [...msgs, ...pending];
          });
          await chatApi.markRead(conversationId);
        } catch (_e) {
          /* ignore */
        }
        return;
      }
      if (payload.conversation_id !== conversationId) return;
      setMessages((prev) => {
        if (prev.some((m) => m.id === payload.id)) return prev;
        return [...prev, payload];
      });
      if (payload.sender_id !== user.id) {
        try {
          await chatApi.markRead(conversationId);
          refreshConversations();
        } catch (_e) {
          /* ignore */
        }
      }
    });
    return unsub;
  }, [conversationId, subscribeToMessages, user, refreshConversations]);

  // Realtime read receipts
  useEffect(() => {
    if (!conversationId) return undefined;
    const unsub = subscribeToReads((evt) => {
      if (evt.conversation_id !== conversationId) return;
      if (evt.reader_id === user.id) return;
      // Prefer finalized_message_ids (group), else mark my messages as read (direct)
      const finalized = evt.finalized_message_ids || [];
      setMessages((prev) =>
        prev.map((m) => {
          if (m.read_at) return m;
          if (finalized.includes(m.id)) return { ...m, read_at: evt.read_at };
          // direct chat fallback
          if (
            conv?.type === "direct" &&
            m.sender_id === user.id &&
            !finalized.length
          ) {
            return { ...m, read_at: evt.read_at };
          }
          return m;
        }),
      );
    });
    return unsub;
  }, [conversationId, subscribeToReads, user, conv]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({
        behavior: hasSeenBottomRef.current ? "smooth" : "auto",
      });
      hasSeenBottomRef.current = true;
    }
  }, [messages]);

  const groups = useMemo(() => groupByDay(messages), [messages]);

  const doSend = async (localId, text) => {
    try {
      const serverMsg = await chatApi.sendMessage(conversationId, text);
      setMessages((prev) => {
        if (prev.some((m) => m.id === serverMsg.id)) {
          return prev.filter((m) => m.id !== localId);
        }
        return prev.map((m) => (m.id === localId ? serverMsg : m));
      });
      refreshConversations();
    } catch (err) {
      setMessages((prev) =>
        prev.map((m) => (m.id === localId ? { ...m, __status: "failed" } : m)),
      );
      toast.error(formatApiError(err));
    }
  };

  const onSend = async (text) => {
    if (!conversationId || !user) return;
    const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const optimistic = {
      id: localId,
      conversation_id: conversationId,
      sender_id: user.id,
      text,
      timestamp: new Date().toISOString(),
      read_at: null,
      read_by: [],
      is_auto: false,
      __status: "sending",
    };
    setMessages((prev) => [...prev, optimistic]);
    // Fire-and-forget telemetry ping. Never awaited — a slow or failing pixel
    // must NEVER affect the message being sent.
    trackMessageSend(user.id);
    await doSend(localId, text);
  };

  const onRetry = async (failedMsg) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === failedMsg.id ? { ...m, __status: "sending" } : m)),
    );
    await doSend(failedMsg.id, failedMsg.text);
  };

  // Typing throttle
  const lastTypingSentRef = useRef(0);
  const onComposerType = () => {
    const now = Date.now();
    if (now - lastTypingSentRef.current > 1800) {
      lastTypingSentRef.current = now;
      sendTyping(conversationId);
    }
  };

  const isGroup = conv?.type === "group";
  const other = conv?.other;
  const pres = other ? getPresence(other.id) : { is_online: false, last_seen: null };
  const online = pres.is_online || (other && other.is_online);
  const typers = getTypingFor(conversationId);
  const membersById = useMemo(() => {
    const map = {};
    if (isGroup && conv?.members) for (const m of conv.members) map[m.id] = m;
    return map;
  }, [isGroup, conv]);

  const senderFor = (msg) => {
    if (isGroup) return membersById[msg.sender_id];
    if (msg.sender_id === user.id) return null;
    return other;
  };

  const typingText = () => {
    if (typers.length === 0) return null;
    if (typers.length === 1) {
      const t = typers[0];
      return isGroup
        ? `${t.display_name.split(" ")[0]} está escribiendo…`
        : "Escribiendo…";
    }
    return "Varios están escribiendo…";
  };

  return (
    <div
      className="max-w-2xl mx-auto pt-[72px] pb-0 h-[100dvh] flex flex-col q-page-enter"
      data-testid="chat-page"
    >
      <div className="q-glass border-b border-white/10 rounded-t-none sm:rounded-t-3xl mt-0 sm:mt-4 mx-0 sm:mx-4 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate("/")}
          className="text-slate-300 hover:text-slate-100 p-1 rounded-full"
          aria-label="Volver"
          data-testid="chat-back"
        >
          <ArrowLeft size={20} />
        </button>
        {!conv ? (
          <div className="flex-1 h-10 q-shimmer rounded-lg" />
        ) : isGroup ? (
          <>
            <div
              className="h-10 w-10 rounded-2xl flex items-center justify-center border border-white/10 bg-gradient-to-br from-slate-700 to-slate-900"
              style={{ boxShadow: "inset 0 0 12px var(--accent-soft)" }}
            >
              <Users size={18} className="q-accent-text" />
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="text-sm font-semibold text-slate-100 truncate"
                data-testid="chat-header-name"
              >
                {conv.title}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <GroupMembersSheet conv={conv} currentUserId={user?.id} />
                {typers.length > 0 && (
                  <span
                    className="text-xs q-accent-text animate-pulse"
                    data-testid="chat-typing"
                  >
                    {typingText()}
                  </span>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="relative">
              <OndaAvatar src={other.avatar} name={other.display_name} size={40} />
              <span className="absolute -bottom-0.5 -right-0.5">
                <PresenceDot isOnline={online} size={11} />
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="text-sm font-semibold text-slate-100 truncate flex items-center gap-1"
                data-testid="chat-header-name"
              >
                {other.display_name}
                {other.is_verified_business && <VerifiedBadge size={13} />}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span
                  className="font-mono text-xs text-slate-400 truncate"
                  data-testid="chat-header-ondatag"
                >
                  @{other.ondatag}
                </span>
                <span className="text-slate-600" aria-hidden="true">·</span>
                {typers.length > 0 ? (
                  <span
                    className="text-xs q-accent-text animate-pulse"
                    data-testid="chat-typing"
                  >
                    {typingText()}
                  </span>
                ) : (
                  <span data-testid="chat-header-presence">
                    <PresenceText isOnline={online} lastSeen={pres.last_seen || other.last_seen} />
                  </span>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <div
        className="flex-1 overflow-y-auto no-scrollbar bg-transparent px-3 sm:px-4 py-4 mx-0 sm:mx-4 border-x-0 sm:border-x border-white/10 q-glass !rounded-none"
        data-testid="chat-scroll"
      >
        {loading && (
          <div className="flex items-center gap-2 text-sm text-slate-300" data-testid="chat-loading">
            <span className="h-2 w-2 rounded-full q-accent-bg animate-pulse" /> Cargando mensajes…
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="text-center text-sm text-slate-400 mt-8" data-testid="chat-empty">
            Aún no hay mensajes. ¡Rompe el hielo con un <span className="q-accent-text">&ldquo;¡qué onda!&rdquo;</span>
          </div>
        )}
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.key}>
              <div className="flex items-center justify-center mb-3">
                <span className="q-chip">{g.label}</span>
              </div>
              <div className="space-y-2">
                {g.items.map((m) => (
                  <MessageBubble
                    key={m.id}
                    message={m}
                    mine={m.sender_id === user.id}
                    sender={senderFor(m)}
                    showSenderInfo={isGroup && m.sender_id !== user.id}
                    onRetry={onRetry}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div ref={bottomRef} />
      </div>

      <div className="mx-0 sm:mx-4 mb-0 sm:mb-4">
        <SmartSuggestions
          conversationId={conversationId}
          currentUserId={user?.id}
          lastMessage={messages[messages.length - 1] || null}
          lastIncomingId={[...messages].reverse().find((m) => m.sender_id !== user?.id)?.id || null}
          onPick={onSend}
        />
        <ChatComposer onSend={onSend} onTyping={onComposerType} disabled={loading || !conv} />
      </div>
    </div>
  );
}
